import React, { useState, useEffect } from 'react';
import { 
  Calendar,
  BarChart3,
  DollarSign,
  Server,
  Brain,
  ChevronDown,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Clock,
  Cpu,
  Zap,
  X
} from 'lucide-react';
import styles from './UsageSettings.module.css';
import billingService from '../../services/billingServices';
import usageService from '../../services/usageServices';
import D3UsageChart from '../../components/D3UsageChart';
import { showSuccessBanner, showErrorBanner } from '../../components/common/ErrorBannerManager';
import ProtectedAction from '../../components/common/ProtectedAction';

export default function UsageSettings() {
  const [selectedTimePeriod, setSelectedTimePeriod] = useState('24h');
  const [activeTab, setActiveTab] = useState('overview');
  const [serverSpendPage, setServerSpendPage] = useState(1);
  const [llmSpendPage, setLlmSpendPage] = useState(1);
  const itemsPerPage = 10;
  const [currentPlan, setCurrentPlan] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resourceUsageLoading, setResourceUsageLoading] = useState(false);
  const [usageData, setUsageData] = useState({
    pod_usage: {
      pods: [],
      total_hours: 0,
      pod_count: 0
    },
    llm_usage: {
      calls: [],
      total_tokens: 0,
      call_count: 0
    },
    subscription: {
      plan_name: null,
      status: null
    },
    period_hours: 24,
    generated_at: null
  });
  const [currentCycleData, setCurrentCycleData] = useState({
    pod_usage: {
      pods: [],
      total_hours: 0,
      pod_count: 0
    },
    llm_usage: {
      calls: [],
      total_tokens: 0,
      call_count: 0
    },
    billing_cycle: {
      start: null,
      end: null,
      days_remaining: 0
    },
    subscription: {
      plan_name: null,
      status: null
    }
  });
  const [chartData, setChartData] = useState([]);
  const [showCreditInput, setShowCreditInput] = useState(false);
  const [creditAmount, setCreditAmount] = useState('25');
  const [isAddingCredits, setIsAddingCredits] = useState(false);
  const [creditBalance, setCreditBalance] = useState({
    available_credits: 0.0,
    ledger_credits: 0.0,
    monthly_subscription_value: 0.0,
    total_available: 0.0,
    currency: 'usd'
  });
  
  // Usage pricing from AWS Secrets Manager via backend API (single source of truth)
  const [usagePricing, setUsagePricing] = useState(null);
  const [pricingLoaded, setPricingLoaded] = useState(false);

  const formatCurrency = (amount) => `$${amount.toFixed(2)}`;
  const formatNumber = (num) => num.toLocaleString();
  const formatHours = (hours) => parseFloat(hours).toFixed(1);
  
  // Format date/time using browser's local timezone and locale
  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // Ensure timestamp is treated as UTC if it doesn't have timezone info
    // Backend sends timestamps as ISO strings without 'Z' suffix
    let dateString = timestamp;
    if (typeof timestamp === 'string' && !timestamp.endsWith('Z') && !timestamp.includes('+')) {
      // Add 'Z' to indicate UTC if no timezone info present
      dateString = timestamp + 'Z';
    }
    
    // Create date object - now it will be parsed as UTC
    let date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    // Format using browser's locale and timezone automatically
    // This will convert from UTC to the user's local timezone
    return date.toLocaleString(undefined, { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true
    });
  };
  
  // Get user's timezone for display
  const getUserTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  // Pagination helpers
  const paginate = (items, currentPage) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.slice(startIndex, endIndex);
  };

  const getTotalPages = (items) => {
    return Math.ceil(items.length / itemsPerPage);
  };

  const renderPagination = (currentPage, totalPages, onPageChange) => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    pages.push(
      <button
        key="prev"
        className={styles.paginationButton}
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        ‹
      </button>
    );

    // First page
    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          className={styles.paginationButton}
          onClick={() => onPageChange(1)}
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis1" className={styles.paginationEllipsis}>...</span>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`${styles.paginationButton} ${currentPage === i ? styles.active : ''}`}
          onClick={() => onPageChange(i)}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis2" className={styles.paginationEllipsis}>...</span>);
      }
      pages.push(
        <button
          key={totalPages}
          className={styles.paginationButton}
          onClick={() => onPageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    // Next button
    pages.push(
      <button
        key="next"
        className={styles.paginationButton}
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        ›
      </button>
    );

    return <div className={styles.pagination}>{pages}</div>;
  };

  // Get rate based on app type (from AWS Secrets Manager via backend API)
  // Single source of truth: backend API pricing data (required, no fallbacks)
  const getAppRate = (appType, instanceType = null, backendHourlyRate = null) => {
    // First priority: Use the backend-provided hourly_rate from PodSession
    // This is set when the pod was created using the pricing config at that time
    if (backendHourlyRate !== null && backendHourlyRate !== undefined && backendHourlyRate > 0) {
      return backendHourlyRate;
    }
    
    // Second priority: Look up from pricing API (required from AWS Secrets Manager)
    const appTypeLower = appType?.toLowerCase();
    const podConfig = usagePricing?.server_pods?.[appTypeLower];
    if (podConfig && podConfig.hourly_rate !== undefined) {
      return podConfig.hourly_rate;
    }
    
    // Last resort: Use default from pricing API (required from AWS Secrets Manager)
    if (usagePricing?.defaults?.hourly_rate !== undefined) {
      return usagePricing.defaults.hourly_rate;
    }
    
    // Pricing not loaded yet - this should only happen during initial load
    console.warn(`[UsageSettings] Pricing not loaded for app type: ${appType}`);
    return null;
  };
  
  // Get LLM token rate based on provider (from AWS Secrets Manager via backend API)
  // Single source of truth: backend API pricing data (required, no fallbacks)
  const getLLMTokenRate = (provider) => {
    const providerLower = provider?.toLowerCase() || 'openai';
    const providerKey = providerLower.includes('anthropic') || providerLower.includes('claude') 
      ? 'anthropic' 
      : 'openai';
    
    // Look up from pricing API (required from AWS Secrets Manager)
    const llmConfig = usagePricing?.llm_tokens?.[providerKey];
    if (llmConfig && llmConfig.price_per_million_tokens !== undefined) {
      return llmConfig.price_per_million_tokens;
    }
    
    // Last resort: Use default from pricing API (required from AWS Secrets Manager)
    if (usagePricing?.defaults?.price_per_million_tokens !== undefined) {
      return usagePricing.defaults.price_per_million_tokens;
    }
    
    // Pricing not loaded yet - this should only happen during initial load
    console.warn(`[UsageSettings] LLM pricing not loaded for provider: ${provider}`);
    return null;
  };

  // Calculate total cost from spend categories
  // Uses backend-provided costs when available (single source of truth from AWS Secrets Manager)
  const calculateTotalSpend = () => {
    let total = 0;
    
    // Add pod usage costs from CURRENT CYCLE data
    // Use backend's total_cost if available, otherwise calculate
    if (currentCycleData.pod_usage?.pods) {
      total += currentCycleData.pod_usage.pods.reduce((sum, pod) => {
        // Prefer backend-provided total_cost (uses correct rates)
        if (pod.total_cost !== undefined && pod.total_cost !== null) {
          return sum + pod.total_cost;
        }
        // Fallback: use backend rate or lookup
        const rate = getAppRate(pod.app_type, pod.instance_type, pod.hourly_rate);
        return sum + ((pod.billable_hours || pod.running_hours) * rate);
      }, 0);
    }
    
    // Add LLM usage costs from CURRENT CYCLE data
    // Use backend's total_cost if available (single source of truth)
    if (currentCycleData.llm_usage?.calls) {
      total += currentCycleData.llm_usage.calls.reduce((sum, call) => {
        // Prefer backend-provided total_cost (uses correct rates from AWS Secrets Manager)
        if (call.total_cost !== undefined && call.total_cost !== null) {
          return sum + call.total_cost;
        }
        // Fallback: use backend rate or lookup
        const pricePerMillion = call.price_per_million_tokens ?? getLLMTokenRate(call.provider);
        return sum + ((call.tokens / 1000000) * pricePerMillion);
      }, 0);
    }
    
    return total;
  };

  // Helper function to convert time period to hours
  const getHoursFromTimePeriod = (period) => {
    const periodMap = {
      '24h': 24,
      '3d': 72,
      '7d': 168,
      '30d': 720,
      '90d': 2160,
      '180d': 4320,
      '365d': 8760
    };
    return periodMap[period] || 24;
  };

  const refreshCreditBalance = async () => {
    try {
      const creditBalanceData = await billingService.getCreditBalance();
      setCreditBalance(creditBalanceData);
    } catch (err) {
      console.error('Failed to refresh credit balance:', err);
    }
  };


  // Load billing data (subscription, plans, credit balance)
  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        const [subscriptionData, plansData, creditBalanceData] = await Promise.all([
          billingService.getCurrentSubscription(),
          billingService.getAvailablePlans(),
          billingService.getCreditBalance()
        ]);

        setCurrentPlan(subscriptionData);
        setAvailablePlans(plansData);
        setCreditBalance(creditBalanceData);

        // Auto-grant subscription credits for existing users with $0 balance
        if (subscriptionData && creditBalanceData?.total_available === 0) {
          console.log('Auto-granting subscription credits for existing user via Credit Grants');
          setTimeout(async () => {
            try {
              const result = await billingService.grantSubscriptionCredits();
              console.log('Credit grant result:', result);
              // Refresh credit balance after granting
              setTimeout(refreshCreditBalance, 2000);  // Give Stripe time to process
            } catch (err) {
              console.error('Failed to auto-grant subscription credits:', err);
            }
          }, 500);
        }

        // Check if user just completed a credit purchase
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('credits') === 'success') {
          // Credit purchase successful, refresh balance after a short delay
          setTimeout(refreshCreditBalance, 2000);
          // Clear the URL parameter
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } catch (err) {
        console.error('Failed to fetch billing data:', err);
        setError('Failed to load billing information');
      }
    };

    fetchBillingData();
  }, []); // Run once on component mount

  // Load usage pricing from AWS Secrets Manager via backend API (single source of truth)
  useEffect(() => {
    const fetchUsagePricing = async () => {
      try {
        const pricingData = await usageService.getUsagePricing();
        if (pricingData) {
          setUsagePricing({
            server_pods: pricingData.server_pods || {},
            llm_tokens: pricingData.llm_tokens || {},
            defaults: pricingData.defaults || {}
          });
          setPricingLoaded(true);
          console.log('Usage pricing loaded from backend:', pricingData);
        }
      } catch (err) {
        console.error('Failed to fetch usage pricing from backend:', err);
        setPricingLoaded(true); // Mark as loaded even on error to unblock UI
      }
    };

    fetchUsagePricing();
  }, []); // Run once on component mount

  // Load current cycle usage data (independent of date picker)
  useEffect(() => {
    const fetchCurrentCycleData = async () => {
      try {
        const currentCycleResponse = await usageService.getCurrentCycleSummary();
        
        if (currentCycleResponse && currentCycleResponse.data) {
          const cycleData = currentCycleResponse.data.data || currentCycleResponse.data;
          setCurrentCycleData({
            pod_usage: cycleData.pod_usage || { pods: [], total_hours: 0, pod_count: 0 },
            llm_usage: cycleData.llm_usage || { calls: [], total_tokens: 0, call_count: 0 },
            billing_cycle: cycleData.billing_cycle || { start: null, end: null, days_remaining: 0 },
            subscription: cycleData.subscription || { plan_name: null, status: null }
          });
        }
      } catch (err) {
        console.error('Failed to fetch current cycle data:', err);
        // Don't set error state here - this is supplementary data
      }
    };

    fetchCurrentCycleData();
  }, []); // Run once on component mount

  // Load usage data based on selected time period
  useEffect(() => {
    const fetchUsageData = async () => {
      try {
        setResourceUsageLoading(true);
        setError(null);
        
        // Fetch usage summary and chart data based on selected time period
        const hours = getHoursFromTimePeriod(selectedTimePeriod);
        const [usageSummary, chartDataResponse] = await Promise.all([
          usageService.getUsageSummary(hours),
          usageService.getUsageChartData(hours)
        ]);
        
        if (usageSummary && usageSummary.data) {
          // Ensure we have the expected data structure
          const data = usageSummary.data.data; // Access the nested data property
          
          setUsageData({
            pod_usage: data.pod_usage || { pods: [], total_hours: 0, pod_count: 0 },
            llm_usage: data.llm_usage || { calls: [], total_tokens: 0, call_count: 0 },
            subscription: data.subscription || { plan_name: null, status: null },
            period_hours: data.period_hours || 24,
            generated_at: data.generated_at || null
          });
        }
        
        if (chartDataResponse && chartDataResponse.data) {
          const chartData = chartDataResponse.data.data || chartDataResponse.data || [];
          setChartData(chartData);
        }
        
      } catch (err) {
        console.error('Failed to fetch usage data:', err);
        setError(err.message || 'Failed to load usage information');
      } finally {
        setResourceUsageLoading(false);
      }
    };

    fetchUsageData();
  }, [selectedTimePeriod]); // Only re-fetch when time period changes

  // Initial load of billing and subscription data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        
        // Fetch billing info
        const subscription = await billingService.getCurrentSubscription();
        setCurrentPlan(subscription);
        const plans = await billingService.getAvailablePlans();
        setAvailablePlans(Array.isArray(plans) ? plans : []);
        
      } catch (err) {
        console.error('Failed to fetch initial data:', err);
        setError(err.message || 'Failed to load billing information');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []); // Run once on component mount

  const handleTimePeriodChange = (period) => {
    setSelectedTimePeriod(period);
  };

  const handleBuyCredits = () => {
    // Show the credit input form - if user can see this page, they have a plan
    setShowCreditInput(true);
  };

  const handleCancelCreditInput = () => {
    setShowCreditInput(false);
    setCreditAmount('25'); // Reset to default
  };

  const handleProceedWithCredits = async () => {
    try {
      const amount = parseFloat(creditAmount);
      
      if (isNaN(amount) || amount <= 0) {
        showErrorBanner({
          title: 'Invalid Amount',
          message: 'Please enter a valid amount greater than $0',
          position: 'top-right'
        });
        return;
      }

      if (amount > 1000) {
        showErrorBanner({
          title: 'Amount Too High',
          message: 'Maximum credit purchase is $1000 per transaction',
          position: 'top-right'
        });
        return;
      }

      setIsAddingCredits(true);

      // URLs for checkout fallback (when no saved payment method)
      const successUrl = window.location.origin + '/settings/usage?credits=success';
      const cancelUrl = window.location.origin + '/settings/usage?credits=cancelled';
      const description = `Account Credit Top-up - $${amount}`;

      // Call the billing service - will try direct charge first, fall back to checkout
      const result = await billingService.createCreditCheckoutSession(
        amount,
        description,
        successUrl,
        cancelUrl
      );
      
      if (result?.direct_purchase) {
        // Direct purchase succeeded - credits added immediately
        console.log('[UsageSettings] Direct credit purchase succeeded:', result);
        setShowCreditInput(false);
        setCreditAmount('25'); // Reset to default
        
        // Refresh credit balance to show new credits
        await refreshCreditBalance();
        
        // Show success banner
        showSuccessBanner({
          title: 'Credits Added!',
          message: `$${amount.toFixed(2)} has been added to your account. Your saved card on file was charged.`,
          position: 'top-right'
        });
      } else if (result?.checkout_url) {
        // Fall back to Stripe checkout (no saved payment method)
        console.log('[UsageSettings] Redirecting to checkout:', result.checkout_url);
        window.location.href = result.checkout_url;
      } else {
        showErrorBanner({
          title: 'Purchase Failed',
          message: 'Failed to process credit purchase. Please try again.',
          position: 'top-right'
        });
      }
      
    } catch (err) {
      console.error('Failed to initiate credit purchase:', err);
      console.error('Error details:', err.message, err.status, err.retCode, err.retmsg);
      
      // Show the actual error message from the backend
      const errorMessage = err.retmsg || err.message || 'Unknown error occurred';
      showErrorBanner({
        title: 'Purchase Failed',
        message: errorMessage,
        position: 'top-right'
      });
    } finally {
      setIsAddingCredits(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading usage information...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p>Error: {error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className={styles.usageSettings}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1>Usage</h1>
          <p className={styles.description}>Monitor your resource consumption and spending patterns</p>
        </div>
      </div>

      <div className={styles.contentGrid}>
        {/* Main Content Area */}
        <div className={styles.mainContent}>
          {/* Usage Overview Section */}
          <div className={styles.spendSection}>
            <div className={styles.spendHeader}>
              <div className={styles.timeFilterPicker}>
                <Calendar size={16} />
                <select 
                  value={selectedTimePeriod} 
                  onChange={(e) => handleTimePeriodChange(e.target.value)}
                  className={styles.timeFilterSelect}
                  disabled={resourceUsageLoading}
                >
                  <option value="24h">Last 24 hours</option>
                  <option value="3d">Last 3 days</option>
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="90d">Last 90 days</option>
                  <option value="180d">Last 180 days</option>
                  <option value="365d">Last 365 days</option>
                </select>
                <ChevronDown size={16} />
              </div>
              <div className={styles.spendAmounts}>
                {resourceUsageLoading ? (
                  <span className={styles.loadingText}>Loading...</span>
                ) : (
                  <>
                    <span className={styles.currentSpend}>
                      {formatHours(
                        (usageData.pod_usage?.pods || []).reduce((sum, pod) => sum + (pod.running_hours || 0), 0)
                      )}h
                    </span>
                    <span className={styles.previousSpend}>{formatNumber(usageData.llm_usage?.total_tokens || 0)} tokens</span>
                  </>
                )}
              </div>
            </div>
            
            {/* Usage Chart */}
            {resourceUsageLoading ? (
              <div className={styles.chartLoadingContainer}>
                <div className={styles.loadingSpinner}></div>
                <p>Refreshing usage data...</p>
              </div>
            ) : (
              <D3UsageChart 
                data={chartData} 
                timePeriod={selectedTimePeriod}
                theme="dark"
              />
            )}

            {/* Tabs */}
            <div className={styles.tabs}>
              <button 
                className={`${styles.tab} ${activeTab === 'overview' ? styles.active : ''}`}
                onClick={() => setActiveTab('overview')}
                disabled={resourceUsageLoading}
              >
                Overview
              </button>
              <button 
                className={`${styles.tab} ${activeTab === 'server-spend' ? styles.active : ''}`}
                onClick={() => setActiveTab('server-spend')}
                disabled={resourceUsageLoading}
              >
                Server Spend
              </button>
              <button 
                className={`${styles.tab} ${activeTab === 'llm-spend' ? styles.active : ''}`}
                onClick={() => setActiveTab('llm-spend')}
                disabled={resourceUsageLoading}
              >
                LLM Spend
              </button>
            </div>

            {/* Content based on active tab */}
            {resourceUsageLoading ? (
              <div className={styles.tabContentLoading}>
                <div className={styles.loadingSpinner}></div>
                <p>Loading data...</p>
              </div>
            ) : (
              <>
                {activeTab === 'overview' && (
              <div className={styles.categoryGrid}>
                <div className={styles.categoryCard}>
                  <div className={styles.categoryHeader}>
                    <Server 
                      size={20} 
                      className={styles.themeAwareIcon}
                    />
                    <span className={styles.categoryName}>Servers</span>
                  </div>
                  <div className={styles.categoryMetrics}>
                    <div className={styles.metric}>
                      <span className={styles.metricValue}>{usageData.pod_usage?.pod_count || 0}</span>
                      <span className={styles.metricLabel}>pods</span>
                    </div>
                    <div className={styles.metric}>
                      <span className={styles.metricValue}>
                        {formatHours(
                          (usageData.pod_usage?.pods || []).reduce((sum, pod) => sum + (pod.running_hours || 0), 0)
                        )}h
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className={styles.categoryCard}>
                  <div className={styles.categoryHeader}>
                    <Brain 
                      size={20} 
                      className={styles.themeAwareIcon}
                    />
                    <span className={styles.categoryName}>LLM Tokens</span>
                  </div>
                  <div className={styles.categoryMetrics}>
                    <div className={styles.metric}>
                      <span className={styles.metricValue}>{usageData.llm_usage?.call_count || 0}</span>
                      <span className={styles.metricLabel}>calls</span>
                    </div>
                    <div className={styles.metric}>
                      <span className={styles.metricValue}>{formatNumber(usageData.llm_usage?.total_tokens || 0)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'server-spend' && (
              <div className={styles.spendCategoriesContent}>
                <div className={styles.spendTableCard}>
                  <div className={styles.tableHeader}>
                    <span className={styles.timezoneIndicator}>
                      <Clock size={14} />
                      All times in {getUserTimezone()}
                    </span>
                    <span className={styles.tableTotalCost}>
                      Total: {formatCurrency(
                        paginate(usageData.pod_usage?.pods || [], serverSpendPage).reduce((sum, pod) => {
                          // Use backend-provided total_cost if available
                          if (pod.total_cost !== undefined && pod.total_cost !== null) {
                            return sum + pod.total_cost;
                          }
                          const rate = getAppRate(pod.app_type, pod.instance_type, pod.hourly_rate);
                          return sum + ((pod.billable_hours || pod.running_hours) * rate);
                        }, 0)
                      )}
                    </span>
                  </div>
                  <div className={styles.spendTable}>
                    <table>
                      <thead>
                        <tr>
                          <th>Start Time</th>
                          <th>End Time</th>
                          <th>Server</th>
                          <th>Quantity</th>
                          <th>Rate</th>
                          <th>Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const pods = usageData.pod_usage?.pods || [];
                          const paginatedPods = paginate(pods, serverSpendPage);
                          
                          if (paginatedPods.length === 0) {
                            return (
                              <tr>
                                <td colSpan="6" className={styles.noData}>No server usage data for this period</td>
                              </tr>
                            );
                          }
                          
                          return paginatedPods.map((pod, index) => {
                            // Use backend-provided hourly_rate (single source of truth)
                            const rate = getAppRate(pod.app_type, pod.instance_type, pod.hourly_rate);
                            // Use backend-provided total_cost if available
                            const cost = (pod.total_cost !== undefined && pod.total_cost !== null)
                              ? pod.total_cost
                              : (pod.billable_hours || pod.running_hours) * rate;
                            
                            return (
                              <tr key={index}>
                                <td className={styles.timestamp}>{formatDateTime(pod.start_time)}</td>
                                <td className={styles.timestamp}>
                                  {pod.end_time ? formatDateTime(pod.end_time) : '-'}
                                </td>
                                <td className={styles.resourceName}>
                                  <Server size={16} className={styles.resourceIcon} />
                                  {pod.app_type}-{pod.pod_name?.split('-')[1] || 'unknown'}
                                </td>
                                <td>{formatHours(pod.billable_hours || pod.running_hours)} hours</td>
                                <td>${rate.toFixed(2)}/hr</td>
                                <td className={styles.cost}>{formatCurrency(cost)}</td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                  {renderPagination(
                    serverSpendPage, 
                    getTotalPages(usageData.pod_usage?.pods || []), 
                    setServerSpendPage
                  )}
                </div>
              </div>
            )}

            {activeTab === 'llm-spend' && (
              <div className={styles.spendCategoriesContent}>
                <div className={styles.spendTableCard}>
                  <div className={styles.tableHeader}>
                    <span className={styles.timezoneIndicator}>
                      <Clock size={14} />
                      All times in {getUserTimezone()}
                    </span>
                    <span className={styles.tableTotalCost}>
                      Total: {formatCurrency(
                        paginate(usageData.llm_usage?.calls || [], llmSpendPage).reduce((sum, call) => {
                          // Use backend-provided total_cost (single source of truth from AWS Secrets Manager)
                          if (call.total_cost !== undefined && call.total_cost !== null) {
                            return sum + call.total_cost;
                          }
                          // Fallback: calculate using backend-provided rate
                          const pricePerMillion = call.price_per_million_tokens ?? getLLMTokenRate(call.provider) ?? 0;
                          return sum + ((call.tokens / 1000000) * pricePerMillion);
                        }, 0)
                      )}
                    </span>
                  </div>
                  <div className={styles.spendTable}>
                    <table>
                      <thead>
                        <tr>
                          <th>Timestamp</th>
                          <th>Provider</th>
                          <th>Model</th>
                          <th>Quantity</th>
                          <th>Rate</th>
                          <th>Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const calls = usageData.llm_usage?.calls || [];
                          const paginatedCalls = paginate(calls, llmSpendPage);
                          
                          if (paginatedCalls.length === 0) {
                            return (
                              <tr>
                                <td colSpan="6" className={styles.noData}>No LLM usage data for this period</td>
                              </tr>
                            );
                          }
                          
                          return paginatedCalls.map((call, index) => {
                            // Use backend-provided pricing (single source of truth from AWS Secrets Manager)
                            // Backend always includes price_per_million_tokens and total_cost
                            const pricePerMillion = call.price_per_million_tokens ?? getLLMTokenRate(call.provider) ?? 0;
                            // Use backend-provided cost if available
                            const cost = (call.total_cost !== undefined && call.total_cost !== null)
                              ? call.total_cost
                              : (call.tokens / 1000000) * pricePerMillion;
                            
                            return (
                              <tr key={index}>
                                <td className={styles.timestamp}>{formatDateTime(call.timestamp || call.created_at)}</td>
                                <td className={styles.resourceName}>
                                  <Brain size={16} className={styles.resourceIcon} />
                                  {call.provider}
                                </td>
                                <td>{call.model}</td>
                                <td>{formatNumber(call.tokens)} tokens</td>
                                <td>${pricePerMillion.toFixed(2)}/1M tokens</td>
                                <td className={styles.cost}>{formatCurrency(cost)}</td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                  {renderPagination(
                    llmSpendPage, 
                    getTotalPages(usageData.llm_usage?.calls || []), 
                    setLlmSpendPage
                  )}
                </div>
              </div>
            )}
              </>
            )}
          </div>
        </div>

        {/* Right Sidebar */}
        <div className={styles.sidebar}>
          {/* Current Plan Section */}
          <div className={styles.sidebarSection}>
            <h3>Current Plan</h3>
            <div className={styles.planBanner}>
              <div className={styles.planHeader}>
                <span className={styles.planName}>{currentPlan?.plan_name || 'No Plan'}</span>
                <span className={`${styles.planStatus} ${currentPlan?.status === 'active' ? styles.active : currentPlan?.status === 'trialing' ? styles.trialing : styles.inactive}`}>
                  {currentPlan?.status === 'active' ? 'ACTIVE' : currentPlan?.status === 'trialing' ? 'TRIAL' : (currentPlan?.status || 'INACTIVE').toUpperCase()}
                </span>
              </div>
              {currentPlan?.next_billing_date && (
                <div className={styles.billingDate}>
                  {currentPlan?.status === 'trialing' ? 'Next billing' : 'Next billing'}: {new Date(currentPlan.next_billing_date).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>

          {/* Credits Section */}
          <div className={styles.sidebarSection}>
            <h3>Current Credits</h3>
            <div className={styles.creditsUsage}>
              <div className={styles.usageAmount}>
                {formatCurrency(calculateTotalSpend())}/{formatCurrency(creditBalance.total_available || 0)} used
              </div>
              <div className={styles.usageBar}>
                <div 
                  className={styles.usageProgress} 
                  style={{ 
                    width: `${Math.min((calculateTotalSpend() / (creditBalance.total_available || 1)) * 100, 100)}%` 
                  }}
                ></div>
              </div>
            </div>
            {!showCreditInput ? (
              <ProtectedAction feature="add_credits">
              <button
                className={styles.buyCreditsButton}
                onClick={handleBuyCredits}
                title="Purchase account credits"
              >
                <RefreshCw size={16} />
                <span>Buy Credits</span>
              </button>
              </ProtectedAction>
            ) : (
              <div className={styles.creditInputForm}>
                <div className={styles.inputGroup}>
                  <span className={styles.dollarSign}>$</span>
                  <input
                    type="number"
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(e.target.value)}
                    className={styles.creditInput}
                    placeholder="25"
                    min="1"
                    max="1000"
                    step="1"
                  />
                </div>
                <div className={styles.inputActions}>
                  <button 
                    className={styles.cancelButton}
                    onClick={handleCancelCreditInput}
                    disabled={isAddingCredits}
                  >
                    <X size={14} />
                    <span>Cancel</span>
                  </button>
                  <button 
                    className={styles.proceedButton}
                    onClick={handleProceedWithCredits}
                    disabled={isAddingCredits}
                  >
                    <span>{isAddingCredits ? 'Adding credits...' : 'Confirm'}</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Server Usage */}
          <div className={styles.sidebarSection}>
            <h3>Current Server Usage</h3>
            <div className={styles.serverInfo}>
              <div className={styles.usageMetric}>
                <span className={styles.metricValue}>{formatHours(currentCycleData.pod_usage?.total_hours || 0)}h</span>
                <span className={styles.metricLabel}>Total Hours</span>
              </div>
              <div className={styles.usageMetric}>
                <span className={styles.metricValue}>{currentCycleData.pod_usage?.pod_count || 0}</span>
                <span className={styles.metricLabel}>Total Servers</span>
              </div>
            </div>
          </div>

          {/* LLM Usage */}
          <div className={styles.sidebarSection}>
            <h3>Current LLM Usage</h3>
            <div className={styles.llmInfo}>
              <div className={styles.usageMetric}>
                <span className={styles.metricValue}>{formatNumber(currentCycleData.llm_usage?.total_tokens || 0)}</span>
                <span className={styles.metricLabel}>Total Tokens</span>
              </div>
              <div className={styles.usageMetric}>
                <span className={styles.metricValue}>{currentCycleData.llm_usage?.call_count || 0}</span>
                <span className={styles.metricLabel}>API Calls</span>
              </div>
            </div>
          </div>

        </div>
      </div>

    </div>
  );
}



